import React, { useState, useEffect } from 'react';
import { Star, MapPin, PawPrint, Bird, Cat, Briefcase, Handshake } from 'lucide-react';
import { Card, CardContent } from './ui/card';

export const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials`);
        const data = await response.json();
        
        // Parse and sort testimonials by date (newest first)
        const sortedData = data.sort((a, b) => {
          // Parse dates like "April 2026", "March 2026", etc.
          const parseDate = (dateStr) => {
            const [month, year] = dateStr.split(' ');
            const monthMap = {
              'January': 0, 'February': 1, 'March': 2, 'April': 3,
              'May': 4, 'June': 5, 'July': 6, 'August': 7,
              'September': 8, 'October': 9, 'November': 10, 'December': 11
            };
            return new Date(parseInt(year), monthMap[month] || 0);
          };
          
          const dateA = parseDate(a.date);
          const dateB = parseDate(b.date);
          
          // Sort newest first (descending)
          return dateB - dateA;
        });
        
        setTestimonials(sortedData);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  // Function to get the appropriate icon based on pet type
  const getPetIcon = (petType) => {
    switch(petType) {
      case 'bird':
        return <Bird className="w-4 h-4" />;
      case 'cat':
        return <Cat className="w-4 h-4" />;
      case 'mixed':
        return (
          <div className="flex items-center gap-1">
            <PawPrint className="w-4 h-4" />
            <span className="text-foreground/40">&</span>
            <Cat className="w-4 h-4" />
          </div>
        );
      case 'dog':
      default:
        return <PawPrint className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <section id="testimonials" className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="w-full h-full bg-secondary/90" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center text-white">Loading testimonials...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="testimonials" className="py-20 relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1530469641172-8ac15d0a7d6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxpbnRlcm5hdGlvbmFsJTIwdHJhdmVsfGVufDB8fHx8MTc3MzI3ODEzOHww&ixlib=rb-4.1.0&q=85"
          alt="International Travel"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-secondary/90" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            What Clients &amp; Partners Say
          </h2>
          <p className="text-base sm:text-lg text-white/80">
            Real experiences from pet owners, brokers, and collaborators we&apos;ve worked with
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {testimonials.map((testimonial) => {
            const kind = testimonial.kind || 'client';
            return (
            <Card key={testimonial.id} className="bg-white/95 backdrop-blur-sm border-0 hover:shadow-2xl transition-all hover:-translate-y-1" data-testid={`testimonial-card-${kind}`}>
              <CardContent className="p-8">
                {/* Kind badge (brokers + collaborators only) */}
                {kind !== 'client' && (
                  <div className="mb-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full border ${
                      kind === 'broker'
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    }`}>
                      {kind === 'broker' ? <Briefcase className="w-3 h-3" /> : <Handshake className="w-3 h-3" />}
                      {kind}
                    </span>
                  </div>
                )}

                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-sm sm:text-base text-foreground/80 leading-relaxed mb-6 italic">
                  &ldquo;{testimonial.text}&rdquo;
                </p>

                {/* Author Info */}
                <div className="border-t border-border pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-base sm:text-lg font-bold text-foreground mb-1">
                        {testimonial.name}
                      </h4>
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/60 mb-2">
                        <MapPin className="w-4 h-4" />
                        <span>{testimonial.location}</span>
                      </div>
                      {kind === 'client' && testimonial.pet ? (
                        <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-primary">
                          {getPetIcon(testimonial.petType)}
                          <span>{testimonial.pet}</span>
                        </div>
                      ) : kind !== 'client' && testimonial.role ? (
                        <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-primary">
                          {kind === 'broker' ? <Briefcase className="w-4 h-4" /> : <Handshake className="w-4 h-4" />}
                          <span>{testimonial.role}</span>
                        </div>
                      ) : null}
                    </div>
                    <div className="text-xs sm:text-sm text-foreground/50">
                      {testimonial.date}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
